import { useEffect, useMemo, useState } from 'react'
import { AccountLink, DrillValue } from '../components/Drill'
import { useTableControls, type Column } from '../lib/useTableControls'
import { TableHead } from '../components/TableControls'
import { DetailGrid, ExpandableRow, useOpenRow } from '../components/RowDetail'
import {
  listConversations,
  getConversation,
  type RemoteConversation,
  type RemoteConversationDetail,
  type ConversationKind
} from '../lib/adminClient'

// Conversations: metadata only. There are deliberately no message bodies here.
// The admin can see who is in a conversation and how much traffic it carries,
// never what was said. That privacy line is stated in the header and repeated in
// the detail panel so it can't be misread.

function fmt(ts: string | number | null): string {
  if (ts === null || ts === undefined || ts === '') return '—'
  const d = typeof ts === 'number' ? new Date(ts) : new Date(ts)
  if (Number.isNaN(d.getTime())) return String(ts)
  return d.toISOString().slice(0, 16).replace('T', ' ') + ' UTC'
}

const KIND_META: Record<ConversationKind, { label: string; tone: string }> = {
  dm: { label: 'DM', tone: 'bg-sky-500/15 text-sky-300' },
  space: { label: 'Space', tone: 'bg-violet-500/15 text-violet-300' },
  channel: { label: 'Channel', tone: 'bg-emerald-500/15 text-emerald-300' }
}

function KindBadge({ kind }: { kind: ConversationKind }): JSX.Element {
  const meta = KIND_META[kind] ?? { label: kind, tone: 'bg-white/5 text-slate-300' }
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] ${meta.tone}`}>{meta.label}</span>
  )
}

const CONVO_COLUMNS: Column<RemoteConversation>[] = [
  { key: 'title', label: 'Conversation', value: (c) => c.title ?? '', filter: 'text' },
  { key: 'kind', label: 'Kind', value: (c) => c.kind },
  { key: 'members', label: 'Members', value: (c) => c.memberCount, numeric: true, className: 'text-right' },
  { key: 'messages', label: 'Messages', value: (c) => c.messageCount, numeric: true, className: 'text-right' },
  { key: 'created', label: 'Created', value: (c) => String(c.createdAt ?? '') },
  { key: 'last', label: 'Last activity', value: (c) => String(c.lastMessageAt ?? '') }
]

export default function Conversations(): JSX.Element {
  const [conversations, setConversations] = useState<RemoteConversation[] | null>(null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<'all' | ConversationKind>('all')
  // The open row is the selected conversation; its metadata loads when it opens.
  const { openId: selected, toggle } = useOpenRow()
  const [detail, setDetail] = useState<RemoteConversationDetail | null>(null)
  const [detailState, setDetailState] = useState<'loading' | 'ready' | 'error'>('loading')
  const [detailError, setDetailError] = useState<string | null>(null)

  async function load(): Promise<void> {
    const res = await listConversations()
    if (res.ok) {
      setConversations(res.data.conversations)
      setState('ready')
    } else {
      setError(res.error)
      setState('error')
    }
  }

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    if (!selected) {
      setDetail(null)
      return
    }
    let cancelled = false
    setDetailState('loading')
    setDetail(null)
    setDetailError(null)
    void getConversation(selected).then((r) => {
      if (cancelled) return
      if (r.ok) {
        setDetail({
          conversation: r.data.conversation,
          members: r.data.members,
          messageStats: r.data.messageStats
        })
        setDetailState('ready')
      } else {
        setDetailError(r.error)
        setDetailState('error')
      }
    })
    return () => {
      cancelled = true
    }
  }, [selected])

  const visible = useMemo(() => {
    const all = conversations ?? []
    const q = query.trim().toLowerCase()
    return all.filter((c) => {
      if (kind !== 'all' && c.kind !== kind) return false
      if (!q) return true
      return (
        (c.title?.toLowerCase().includes(q) ?? false) ||
        c.id.toLowerCase().includes(q) ||
        (c.orgId?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [conversations, query, kind])

  const t = useTableControls(visible, CONVO_COLUMNS, 'conversations')

  const header = (
    <div className="mb-6 flex items-end justify-between gap-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Chat</div>
        <h1 className="text-2xl font-semibold mt-1">
          Conversations
          {conversations ? (
            <span className="text-slate-400 text-base ml-2 font-normal">
              {visible.length} of {conversations.length}
            </span>
          ) : null}
        </h1>
        <div className="text-sm text-slate-400 mt-1">
          Direct messages, spaces, and channels. Metadata only. Message contents are never shown here, by design.
        </div>
      </div>
    </div>
  )

  if (state !== 'ready') {
    return (
      <div>
        {header}
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {state === 'loading' ? 'Loading conversations…' : `Could not load conversations: ${error}`}
        </div>
      </div>
    )
  }

  return (
    <div>
      {header}

      <div className="glass-card rounded-xl p-4 mb-4 flex flex-wrap gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search title, conversation id, or org id…"
          className="flex-1 min-w-[260px] bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        />
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as 'all' | ConversationKind)}
          className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-accent"
        >
          <option value="all">All kinds</option>
          <option value="dm">DMs</option>
          <option value="space">Spaces</option>
          <option value="channel">Channels</option>
        </select>
      </div>

      {visible.length === 0 ? (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-400">
          {(conversations ?? []).length === 0
            ? 'No conversations yet.'
            : 'No conversations match your filters.'}
        </div>
      ) : (
        <div className="glass-card rounded-xl overflow-hidden">
          <table className="admin-table">
          <TableHead t={t} />
            <tbody>
              {t.rows.map((c) => (
                <ExpandableRow
                  key={c.id}
                  open={selected === c.id}
                  onToggle={() => toggle(c.id)}
                  colSpan={CONVO_COLUMNS.length}
                  testId={`conversation-row-${c.id}`}
                  detail={() => (
                    <ConversationDetail
                      c={c}
                      detail={detail}
                      detailState={detailState}
                      detailError={detailError}
                    />
                  )}
                >
                  <td>
                    <div className="font-medium text-slate-100">{c.title || (c.kind === 'dm' ? 'Direct message' : 'Untitled')}</div>
                    <div className="text-[11px] text-slate-500">{c.id}{c.orgId ? ` · org ${c.orgId}` : ''}</div>
                  </td>
                  <td><KindBadge kind={c.kind} /></td>
                  <td className="text-right tabular-nums text-slate-300">
                    <DrillValue to={{ set: 'conversationMembers', filters: { conversation: c.id } }} label={`Members of ${c.title || 'a conversation'}`} expect={{ key: 'count', value: c.memberCount }}>{c.memberCount}</DrillValue>
                  </td>
                  <td className="text-right tabular-nums text-slate-300">
                    <DrillValue to={{ set: 'messages', filters: { conversation: c.id } }} label={`Messages in ${c.title || 'a conversation'}`} expect={{ key: 'count', value: c.messageCount }} testId={`conversation-messages-${c.id}`}>{c.messageCount.toLocaleString()}</DrillValue>
                  </td>
                  <td className="text-slate-400 text-xs">{fmt(c.createdAt)}</td>
                  <td className="text-slate-400 text-xs">{fmt(c.lastMessageAt)}</td>
                </ExpandableRow>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  )
}

const KIND_MEANING: Record<ConversationKind, string> = {
  dm: 'Direct message between two people',
  space: 'Space: a group conversation',
  channel: 'Channel in an organisation'
}

// One conversation's metadata. Message contents are never shown: the server
// does not hold them for admin review.
function ConversationDetail({
  c,
  detail,
  detailState,
  detailError
}: {
  c: RemoteConversation
  detail: RemoteConversationDetail | null
  detailState: 'loading' | 'ready' | 'error'
  detailError: string | null
}): JSX.Element {
  return (
    <DetailGrid
      facts={[
        ['Title', c.title || (c.kind === 'dm' ? 'Direct message (no title)' : 'Untitled')],
        ['Kind', KIND_META[c.kind]?.label ?? c.kind],
        ['What that means', KIND_MEANING[c.kind] ?? null],
        ['Conversation id', <span className="font-mono">{c.id}</span>],
        ['Organisation id', c.orgId ? <span className="font-mono">{c.orgId}</span> : 'None (not in an organisation)'],
        ['Members', <DrillValue to={{ set: 'conversationMembers', filters: { conversation: c.id } }} label={`Members of ${c.title || 'a conversation'}`} expect={{ key: 'count', value: c.memberCount }}>{c.memberCount.toLocaleString()}</DrillValue>],
        ['Messages', <DrillValue to={{ set: 'messages', filters: { conversation: c.id } }} label={`Messages in ${c.title || 'a conversation'}`} expect={{ key: 'count', value: c.messageCount }}>{c.messageCount.toLocaleString()}</DrillValue>],
        ['Created', fmt(c.createdAt)],
        ['Last activity', fmt(c.lastMessageAt)]
      ]}
    >
      <div className="w-full" data-testid="conversation-detail-stats">
        {detailState === 'loading' && <div className="text-sm text-slate-400">Loading detail…</div>}
        {detailState === 'error' && (
          <div className="text-sm text-red-300">Could not load detail: {detailError}</div>
        )}
        {detailState === 'ready' && detail && (
          <div className="space-y-4">
            <div className="text-[11px] text-slate-500">
              Metadata only. Message contents are not stored for admin review and cannot be shown here.
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div className="glass-card rounded-lg p-3 border border-white/5">
                <div className="uppercase tracking-wider text-slate-400 mb-0.5">Messages</div>
                <div className="text-slate-100 text-base">{detail.messageStats.total.toLocaleString()}</div>
              </div>
              <div className="glass-card rounded-lg p-3 border border-white/5">
                <div className="uppercase tracking-wider text-slate-400 mb-0.5">Deleted</div>
                <div className="text-slate-100 text-base">{detail.messageStats.deleted.toLocaleString()}</div>
              </div>
              <div className="glass-card rounded-lg p-3 border border-white/5">
                <div className="uppercase tracking-wider text-slate-400 mb-0.5">Distinct senders</div>
                <div className="text-slate-100 text-base">{detail.messageStats.senderCount.toLocaleString()}</div>
              </div>
              <div className="glass-card rounded-lg p-3 border border-white/5">
                <div className="uppercase tracking-wider text-slate-400 mb-0.5">Members</div>
                <div className="text-slate-100 text-base">{detail.members.length.toLocaleString()}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs text-slate-400">
              <div>
                <div className="uppercase tracking-wider mb-0.5">First message</div>
                <div className="text-slate-200">{fmt(detail.messageStats.firstMessageAt)}</div>
              </div>
              <div>
                <div className="uppercase tracking-wider mb-0.5">Last message</div>
                <div className="text-slate-200">{fmt(detail.messageStats.lastMessageAt)}</div>
              </div>
            </div>

            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-400 mb-2">Members</div>
              {detail.members.length === 0 ? (
                <div className="text-sm text-slate-500">No members reported.</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {detail.members.map((m) => (
                    <span
                      key={m.accountId}
                      className="px-2 py-1 rounded-full text-xs bg-white/5 text-slate-200"
                      title={m.email || m.accountId}
                    >
                      <AccountLink id={m.accountId}>{m.displayName || (m.handle ? `@${m.handle}` : m.email || m.accountId)}</AccountLink>
                      {m.email && (m.displayName || m.handle) && <span className="text-slate-400"> · {m.email}</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DetailGrid>
  )
}
